import { useEffect, useMemo, useRef, useState } from "react";
import useUserDetails from "../../../../utils/hooks/user/useUserDetails";
import Form from "../../../forms/Form";
import ControlledDropdown from "../../../forms/inputs/ControlledDropdown";
import countryList from "react-select-country-list";
import ResearcherTextBox from "../../inputs/ResearcherTextBox";
import SettingsTextArea from "../../inputs/SettingsTextArea";
import SecondaryButton from "../../../buttons/SecondaryButton";
import ProfileImageUploader, {
  ProfileImageFieldValues
} from "../../inputs/ProfileImageUploader";
import toast from "react-hot-toast";
import ProfileIcon from "../../../../assets/icons/ProfileIcon";
import ToggleRow from "../../../forms/inputs/ToggleRow";
import { useUpdateUserNotificationsMutation } from "../../../../utils/api/endpoints/usersApi";
import { UserDetails } from "../../../../utils/store/reducer/slices/userReducer";
import NotificationSettings from "../../NotificationSettings";

/**
 * Unified researcher account settings:
 * - Profile picture
 * - Username, Display Name, Country
 * - Role, Company (read-only)
 * - About/Bio
 */
const ResearcherAccount = () => {
  const { username, country, displayName, about, email_notifications_enabled, setDetails, user } = useUserDetails();
  const countries = useMemo(() => countryList().getData(), []);
  const [profileUpdated, setProfileUpdated] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const formWrapRef = useRef<HTMLDivElement | null>(null);

  const defaultValues = useMemo(
    () => ({
      username: username || "",
      displayName: displayName || "",
      country,
      about: about || "",
      profilePicture: [] as Blob[],
      bannerPicture: [] as Blob[]
    }),
    [username, displayName, country, about]
  );

  type FormData = typeof defaultValues;

  const handleSubmit = (data: FormData) => {
    setDetails({
      username: data.username,
      displayName: data.displayName,
      country: data.country,
      about: data.about,
      profilePicture:
        data.profilePicture && data.profilePicture.length > 0
          ? data.profilePicture[0]
          : undefined,
      bannerPicture:
        data.bannerPicture && data.bannerPicture.length > 0
          ? data.bannerPicture[0]
          : undefined
    })
      .then(() => {
        setProfileUpdated(true);
        toast.success("Account updated!");
      })
      .catch(() => {
        toast.error("Failed to update account");
      });
  };

  // Auto-submit the form right after cropping/selection completes
  useEffect(() => {
    const handler = (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target) return;
      if (target.getAttribute("name") === "profilePicture") {
        // submit the nearest form within this component
        const container = formWrapRef.current;
        const form = container?.querySelector("form") as HTMLFormElement | null;
        if (form) {
          if (typeof (form as any).requestSubmit === "function") (form as any).requestSubmit();
          else form.submit();
        }
        setShowCropper(false);
      }
    };
    document.addEventListener("change", handler, true);
    return () => document.removeEventListener("change", handler, true);
  }, []);

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        {user?.profilePicture ? (
          <img
            src={user.profilePicture}
            alt="Profile"
            className="w-20 h-20 rounded-full object-cover border-4 border-[#010D2C] shadow-lg"
          />
        ) : (
          <div className="w-20 h-20 rounded-full flex items-center justify-center border-4 border-[#010D2C] bg-white shadow-lg">
            <ProfileIcon className="h-10 w-10 stroke-[#010D2C]" />
          </div>
        )}
        <div>
          <SecondaryButton
            type="button"
            className="text-sm"
            onClick={() => {
              setShowCropper(true);
              // Find and click the file input inside ProfileImageUploader
              setTimeout(() => {
                const fileInput = document.querySelector('input[type="file"][accept*="jpg"]') as HTMLInputElement;
                if (fileInput) fileInput.click();
              }, 100);
            }}
          >
            Change Photo
          </SecondaryButton>
        </div>
      </div>

      {username && (
        <div ref={formWrapRef}>
        <Form
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Profile picture uploader - visible when cropping; auto-submit on change */}
          <div className={showCropper ? "mb-6" : "hidden"}>
            <ProfileImageUploader 
              imageUpToDate={profileUpdated}
              onAfterChange={() => {
                // submit the form after crop completes
                const form = formWrapRef.current?.querySelector("form") as HTMLFormElement | null;
                if (form) {
                  if (typeof (form as any).requestSubmit === "function") (form as any).requestSubmit();
                  else form.submit();
                }
                setShowCropper(false);
              }}
            />
          </div>

          {/* Two-column grid for form fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <ResearcherTextBox
                name="displayName"
                placeholder="Enter display name"
                rules={{ required: false }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <ResearcherTextBox
                name="username"
                placeholder="Enter username"
                rules={{ required: true }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <div className="text-gray-800 text-sm py-2 border-b border-gray-300">Capture The Bug</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <ControlledDropdown
                name="country"
                placeholder="Select a country"
                options={countries.map(c => ({
                  value: c.label
                }))}
                className="w-full border-0 border-b border-gray-300 rounded-none focus:ring-0 focus:border-blue-600 px-0 py-2 bg-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">About</label>
            <SettingsTextArea
              name="about"
              placeholder="Tell us about yourself..."
              rows={4}
              className="w-full border-0 border-b border-gray-300 rounded-none focus:ring-0 focus:border-blue-600 px-0 py-2 bg-transparent"
            />
          </div>

          <SecondaryButton type="submit" className="mt-6">
            Save Changes
          </SecondaryButton>
        </Form>

        </div>
      )}
    </div>
  );
};

export default ResearcherAccount;
